package top.wcpe.allincore.ui.web.asset

import kotlin.js.Promise

internal data class ResolvedAsset<T>(
    val canonicalPath: String,
    val actualBytes: Long,
    val mime: String,
    val contentVersion: String,
    val content: T,
    val fileProvenance: CanonicalAssetFileProvenance,
)

internal class ObjectUrlLease internal constructor(
    val url: String,
    val canonicalPath: String,
    val contentVersion: String,
    val mime: String,
    private val release: () -> Boolean,
) {
    private var active = true

    fun dispose() {
        tryDispose()
    }

    internal fun tryDispose(): Boolean {
        if (!active) return true
        val released = release()
        if (released) active = false
        return released
    }
}

internal class PreviewBudgetRetirement internal constructor(
    private val release: () -> Unit,
) {
    private var eligible = false
    private var released = false

    internal fun activate() {
        if (!released) eligible = true
    }

    fun dispose() {
        if (!eligible || released) return
        release()
        released = true
    }
}

internal class CanonicalAssetFileProvenance internal constructor(
    val canonicalPath: String,
    internal val ownerStateId: Long,
)

internal class CanonicalAssetDirectoryProvenance internal constructor(
    val canonicalDirectory: String,
    internal val ownerStateId: Long,
)

internal class PreviewAssetResolver(
    private val sourceAdapter: AssetSourceAdapter,
    private val objectUrlBackend: AssetObjectUrlBackend,
    private val jpegChecker: AssetJpegIntegrityChecker,
    currentBytes: Long = 0,
) {
    private var stateIdSequence = 1L
    private var publishedState = GenerationReadState(stateIdSequence++)
    private var candidateState: GenerationReadState? = null
    private val pendingReads = mutableSetOf<PendingRead>()
    private val versionReads = mutableSetOf<AssetSourceReadOperation>()
    private val objectUrls = mutableMapOf<ObjectUrlKey, SharedObjectUrl>()
    private val budget = AssetBudget(currentBytes)
    private var candidateBudgetRetirement: PreviewBudgetRetirement? = null
    private var publishedBudgetRetirement: PreviewBudgetRetirement? = null
    private var disposed = false
    private var generationValue = 1L

    val generation: Long
        get() = generationValue

    fun resolveText(path: String): Promise<ResolvedAsset<String>> {
        val canonicalPath = externalPath(path)
        val resolved =
            resolve(canonicalPath, TEXT_MIME, AssetBudgetClass.TEXT, PreviewReadKind.ROOT_SOURCE, writeState())
        return mapResolved(resolved) { asset ->
            ResolvedAsset(
                asset.canonicalPath,
                asset.actualBytes,
                asset.mime,
                asset.contentVersion,
                asset.content.decodeToString(),
                asset.fileProvenance,
            )
        }
    }

    fun resolveBytes(path: String): Promise<ResolvedAsset<ByteArray>> {
        val canonicalPath = externalPath(path)
        return resolve(canonicalPath, null, AssetBudgetClass.AICR, PreviewReadKind.ROOT_BYTES, writeState())
    }

    internal fun resolveCanonical(path: String, kind: AssetReferenceKind): Promise<ResolvedAsset<ByteArray>> =
        resolveInternal(externalPath(path), kind, writeState())

    fun resolveRelative(
        referrerCanonicalPath: String,
        relativePath: String,
        kind: AssetReferenceKind,
    ): Promise<ResolvedAsset<ByteArray>> {
        val owner = try {
            stateOwningReferrer(referrerCanonicalPath)
        } catch (error: Throwable) {
            return Promise.reject(error)
        }
        return resolveInternal(relativeFilePath(referrerCanonicalPath, relativePath), kind, owner)
    }

    fun resolveRelative(
        provenance: CanonicalAssetFileProvenance,
        relativePath: String,
        kind: AssetReferenceKind,
    ): Promise<ResolvedAsset<ByteArray>> {
        val owner = stateById(provenance.ownerStateId) ?: return rejected("内部资产 file provenance 已失效")
        if (provenance.canonicalPath !in owner.referrers) {
            return rejected("内部资产 canonical file provenance 未登记")
        }
        return resolveInternal(relativeFilePath(provenance.canonicalPath, relativePath), kind, owner)
    }

    fun resolveRelative(
        provenance: CanonicalAssetDirectoryProvenance,
        relativePath: String,
        kind: AssetReferenceKind,
    ): Promise<ResolvedAsset<ByteArray>> {
        val owner = stateById(provenance.ownerStateId) ?: return rejected("内部资产 directory provenance 已失效")
        if (!owner.ownsDirectory(provenance.canonicalDirectory)) {
            return rejected("内部资产 canonical directory provenance 未登记")
        }
        return resolveInternal(relativeDirectoryPath(provenance.canonicalDirectory, relativePath), kind, owner)
    }

    fun canonicalDirectoryProvenance(canonicalDirectory: String): CanonicalAssetDirectoryProvenance {
        ensureNotDisposed()
        val normalized = AssetPathPolicy.canonicalDirectory(canonicalDirectory)
        val owner = stateOwningDirectory(normalized)
        return CanonicalAssetDirectoryProvenance(normalized, owner.id)
    }

    internal fun resolveRegistered(
        referrerCanonicalPath: String,
        canonicalPath: String,
        kind: AssetReferenceKind,
    ): Promise<ResolvedAsset<ByteArray>> {
        val owner = try {
            stateOwningReferrer(referrerCanonicalPath)
        } catch (error: Throwable) {
            return Promise.reject(error)
        }
        return resolveRegistered(canonicalPath, kind, owner)
    }

    internal fun resolveRegistered(
        provenance: CanonicalAssetFileProvenance,
        canonicalPath: String,
        kind: AssetReferenceKind,
    ): Promise<ResolvedAsset<ByteArray>> {
        val owner = stateById(provenance.ownerStateId) ?: return rejected("内部资产 file provenance 已失效")
        if (provenance.canonicalPath !in owner.referrers) {
            return rejected("内部资产 canonical file provenance 未登记")
        }
        return resolveRegistered(canonicalPath, kind, owner)
    }

    private fun resolveRegistered(
        canonicalPath: String,
        kind: AssetReferenceKind,
        owner: GenerationReadState,
    ): Promise<ResolvedAsset<ByteArray>> {
        val normalized = try {
            AssetPathPolicy.external(canonicalPath)
        } catch (error: Throwable) {
            return Promise.reject(error)
        }
        return resolveInternal(normalized, kind, owner)
    }

    internal fun resolveOptionalRegistered(
        provenance: CanonicalAssetFileProvenance,
        canonicalPath: String,
        kind: AssetReferenceKind,
    ): Promise<ResolvedAsset<ByteArray>?> {
        val owner = stateById(provenance.ownerStateId) ?: return rejected("内部资产 file provenance 已失效")
        if (provenance.canonicalPath !in owner.referrers) return rejected("内部资产 canonical file provenance 未登记")
        val normalized = try {
            AssetPathPolicy.external(canonicalPath)
        } catch (error: Throwable) {
            return Promise.reject(error)
        }
        return optionalResolved(normalized, kind, owner)
    }

    fun probePublishedReadSetVersions(): Promise<Set<String>> {
        ensureNotDisposed()
        if (candidateState != null) return rejected("候选读取期间不可探测 published 版本")
        val generation = generationValue
        val stateId = publishedState.id
        val entries = publishedProbeEntries()
        return probeEntries(entries, 0, linkedSetOf(), generation, stateId)
    }

    fun readSetSnapshot(): List<PreviewReadAsset> = publishedState.readSet.values.toList()

    internal fun candidateReadSetSnapshot(): List<PreviewReadAsset> =
        candidateState?.readSet?.values?.toList().orEmpty()

    fun acquireObjectUrl(path: String, expectedMime: String): Promise<ObjectUrlLease> {
        val canonicalPath = externalPath(path)
        val generation = generationValue
        return mapResolved(
            resolve(canonicalPath, expectedMime, AssetBudgetClass.IMAGE, PreviewReadKind.ROOT_BYTES, writeState()),
        ) { asset ->
            ensureActive(generation)
            acquireLease(asset)
        }
    }

    internal fun acquireObjectUrl(asset: ResolvedAsset<ByteArray>): ObjectUrlLease {
        ensureNotDisposed()
        return acquireLease(asset)
    }

    fun reload(changedPaths: Set<String>? = null) {
        ensureNotDisposed()
        val normalizedChangedPaths = changedPaths?.map(AssetPathPolicy::external)
        advanceGeneration(startCandidate = true)
        val candidate = candidateState ?: return
        candidate.ownCache.putAll(publishedState.ownCache)
        if (normalizedChangedPaths == null) {
            candidate.ownCache.clear()
            return
        }
        normalizedChangedPaths.forEach(candidate.ownCache::remove)
    }

    fun cancel() {
        ensureNotDisposed()
        advanceGeneration(startCandidate = false)
    }

    internal fun beginCandidate() {
        ensureNotDisposed()
        if (candidateState == null) advanceGeneration(startCandidate = true)
    }

    internal val retainedBudgetBytes: Long
        get() = budget.currentBytes + budget.generationBytes

    internal fun candidateBudgetRetirement(expectedGeneration: Long): PreviewBudgetRetirement {
        ensureActive(expectedGeneration)
        return candidateBudgetRetirement ?: error("候选预算退休资源不存在")
    }

    internal fun publishCandidate(): PreviewBudgetRetirement? {
        ensureNotDisposed()
        val candidate = candidateState ?: return null
        val previousPublished = publishedState
        val previousRetirement = publishedBudgetRetirement
        publishedState = candidate
        previousPublished.clear()
        candidateState = null
        budget.publishGeneration()
        previousRetirement?.activate()
        publishedBudgetRetirement = candidateBudgetRetirement
        candidateBudgetRetirement = null
        return previousRetirement
    }

    internal fun discardCandidate() {
        if (disposed || candidateState == null) return
        advanceGeneration(startCandidate = false)
    }

    fun dispose() {
        val errors = mutableListOf<Throwable>()
        if (!disposed) {
            disposed = true
            generationValue += 1
            revokeObjectUrls(errors)
            cleanupResolverState(errors)
        } else {
            revokeObjectUrls(errors)
        }
        throwCleanupErrors(errors)
    }

    private fun revokeObjectUrls(errors: MutableList<Throwable>) {
        objectUrls.toList().forEach { (key, shared) ->
            captureCleanupError(errors) {
                objectUrlBackend.revoke(shared.url)
                objectUrls.remove(key)
            }
        }
    }

    private fun cleanupResolverState(errors: MutableList<Throwable>) {
        captureCleanupError(errors) { cancelPending() }
        pendingReads.clear()
        captureCleanupError(errors) { publishedState.clear() }
        captureCleanupError(errors) { candidateState?.clear() }
        candidateState = null
        candidateBudgetRetirement = null
        publishedBudgetRetirement = null
        captureCleanupError(errors) { budget.dispose() }
        captureCleanupError(errors) { sourceAdapter.dispose() }
    }

    private fun resolveInternal(
        canonicalPath: String,
        kind: AssetReferenceKind,
        owner: GenerationReadState,
    ): Promise<ResolvedAsset<ByteArray>> = try {
        validateReferenceKind(canonicalPath, kind)
        resolve(canonicalPath, kind.expectedMime(), kind.preflightClass(), kind.toPreviewReadKind(), owner)
    } catch (error: Throwable) {
        Promise.reject(error)
    }

    private fun optionalResolved(
        canonicalPath: String,
        kind: AssetReferenceKind,
        owner: GenerationReadState,
    ): Promise<ResolvedAsset<ByteArray>?> = resolveInternal(canonicalPath, kind, owner).then(
        onFulfilled = { asset -> asset },
        onRejected = { error -> handleOptionalFailure(canonicalPath, kind, owner, error) },
    )

    private fun handleOptionalFailure(
        canonicalPath: String,
        kind: AssetReferenceKind,
        owner: GenerationReadState,
        error: Throwable,
    ): ResolvedAsset<ByteArray>? {
        if (!error.isNotFound()) throw error
        ensureStateOwned(owner)
        owner.optionalMissing[canonicalPath] = kind.toPreviewReadKind()
        return null
    }

    private fun publishedProbeEntries(): List<VersionProbeEntry> {
        val present = publishedState.readSet.values.map {
            VersionProbeEntry(it.canonicalPath, it.kind, it.contentVersion)
        }
        val missing = publishedState.optionalMissing.map { (path, kind) -> VersionProbeEntry(path, kind, null) }
        return present + missing
    }

    private fun probeEntries(
        entries: List<VersionProbeEntry>,
        index: Int,
        changed: MutableSet<String>,
        generation: Long,
        stateId: Long,
    ): Promise<Set<String>> {
        if (index >= entries.size) return Promise.resolve(changed.toSet())
        return probeEntry(entries[index], generation, stateId).flatMap { differs ->
            if (differs) changed += entries[index].canonicalPath
            probeEntries(entries, index + 1, changed, generation, stateId)
        }
    }

    private fun probeEntry(entry: VersionProbeEntry, generation: Long, stateId: Long): Promise<Boolean> {
        val operation = try {
            sourceAdapter.read(entry.canonicalPath, entry.kind.probeReadLimit())
        } catch (error: Throwable) {
            return Promise.reject(error)
        }
        versionReads += operation
        return operation.result.then(
            onFulfilled = { read -> completeProbe(operation, entry, read, generation, stateId) },
            onRejected = { error -> failProbe(operation, entry, error, generation, stateId) },
        )
    }

    private fun completeProbe(
        operation: AssetSourceReadOperation,
        entry: VersionProbeEntry,
        read: AssetSourceRead,
        generation: Long,
        stateId: Long,
    ): Boolean {
        versionReads -= operation
        ensureProbeActive(generation, stateId)
        return entry.contentVersion != contentVersion(read)
    }

    private fun failProbe(
        operation: AssetSourceReadOperation,
        entry: VersionProbeEntry,
        error: Throwable,
        generation: Long,
        stateId: Long,
    ): Boolean {
        versionReads -= operation
        ensureProbeActive(generation, stateId)
        if (error.isNotFound()) return entry.contentVersion != null
        throw error
    }

    private fun ensureProbeActive(generation: Long, stateId: Long) {
        ensureActive(generation)
        if (publishedState.id != stateId) failResolver("published 读取集版本探测已失效")
    }

    private fun registerRead(owner: GenerationReadState, asset: ResolvedAsset<ByteArray>, kind: PreviewReadKind) {
        owner.optionalMissing.remove(asset.canonicalPath)
        owner.readSet[asset.canonicalPath] = PreviewReadAsset(
            asset.canonicalPath,
            kind,
            asset.contentVersion,
            asset.actualBytes,
        )
    }

    private fun resolve(
        canonicalPath: String,
        expectedMime: String?,
        preflightClass: AssetBudgetClass,
        readKind: PreviewReadKind,
        owner: GenerationReadState,
    ): Promise<ResolvedAsset<ByteArray>> {
        if (disposed) return rejected("资产解析器已释放")
        val generation = generationValue
        val cached = owner.ownCache[canonicalPath]
        val resolved = if (cached == null) {
            readAndValidate(canonicalPath, expectedMime, preflightClass, generation, owner)
        } else {
            validateCached(canonicalPath, cached, expectedMime, preflightClass, generation, owner)
        }
        return resolved.then { asset ->
            ensureActive(generation)
            ensureStateOwned(owner)
            registerRead(owner, asset, readKind)
            asset
        }
    }

    private fun readAndValidate(
        canonicalPath: String,
        expectedMime: String?,
        preflightClass: AssetBudgetClass,
        generation: Long,
        owner: GenerationReadState,
    ): Promise<ResolvedAsset<ByteArray>> {
        val reservation = reserve(canonicalPath, preflightClass, null)
        val readLimit = readLimit(preflightClass)
        val operation = try {
            sourceAdapter.read(canonicalPath, readLimit)
        } catch (error: Throwable) {
            reservation.cancel()
            return Promise.reject(error)
        }
        val pending = PendingRead(operation, reservation)
        pendingReads += pending
        return Promise { resolve, reject ->
            operation.result.then(
                onFulfilled = { read ->
                    completeRead(
                        canonicalPath,
                        expectedMime,
                        generation,
                        owner,
                        pending,
                        readLimit,
                        read,
                        resolve,
                        reject,
                    )
                },
                onRejected = { error -> rejectRead(pending, error, reject) },
            )
        }
    }

    private fun completeRead(
        canonicalPath: String,
        expectedMime: String?,
        generation: Long,
        owner: GenerationReadState,
        pending: PendingRead,
        readLimit: AssetReadLimit,
        read: AssetSourceRead,
        resolve: (ResolvedAsset<ByteArray>) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        try {
            ensureActive(generation)
            val cached = CachedAsset(read.bytes, read.declaredMime, contentVersion(read))
            validateReadSize(read, readLimit)
            validateContent(canonicalPath, cached, expectedMime, generation, owner, pending, resolve, reject)
        } catch (error: Throwable) {
            rejectRead(pending, error, reject)
        }
    }

    private fun validateCached(
        canonicalPath: String,
        cached: CachedAsset,
        expectedMime: String?,
        preflightClass: AssetBudgetClass,
        generation: Long,
        owner: GenerationReadState,
    ): Promise<ResolvedAsset<ByteArray>> {
        val key = AssetBudgetKey.Resource(canonicalPath, cached.contentVersion)
        val retained = owner.readSet[canonicalPath]?.contentVersion == cached.contentVersion
        val reservation = if (retained) {
            budget.reserveRetained(key, preflightClass, cached.bytes.size.toLong())
        } else {
            budget.reserveKnown(key, preflightClass, cached.bytes.size.toLong())
        }
        val pending = PendingRead(null, reservation)
        pendingReads += pending
        return Promise { resolve, reject ->
            try {
                validateContent(canonicalPath, cached, expectedMime, generation, owner, pending, resolve, reject)
            } catch (error: Throwable) {
                rejectRead(pending, error, reject)
            }
        }
    }

    private fun validateContent(
        canonicalPath: String,
        cached: CachedAsset,
        expectedMime: String?,
        generation: Long,
        owner: GenerationReadState,
        pending: PendingRead,
        resolve: (ResolvedAsset<ByteArray>) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        if (canonicalPath.isJpeg()) {
            val operation = jpegChecker.check(cached.bytes)
            pending.jpegOperation = operation
            if (pending.cancelled) operation.cancel()
            operation.result.then(
                onFulfilled = {
                    completeValidation(
                        canonicalPath,
                        cached,
                        expectedMime,
                        generation,
                        owner,
                        pending,
                        it,
                        resolve,
                        reject,
                    )
                },
                onRejected = { error -> rejectRead(pending, error, reject) },
            )
            return
        }
        completeValidation(canonicalPath, cached, expectedMime, generation, owner, pending, true, resolve, reject)
    }

    private fun completeValidation(
        canonicalPath: String,
        cached: CachedAsset,
        expectedMime: String?,
        generation: Long,
        owner: GenerationReadState,
        pending: PendingRead,
        jpegComplete: Boolean,
        resolve: (ResolvedAsset<ByteArray>) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        try {
            ensureActive(generation)
            ensureStateOwned(owner)
            val descriptor = AssetContentPolicy.validate(
                AssetContentRequest(
                    canonicalPath,
                    sourceAdapter.source,
                    cached.declaredMime,
                    expectedMime,
                    cached.bytes,
                ),
                JpegDecodeChecker { jpegComplete },
            )
            val key = AssetBudgetKey.Resource(canonicalPath, cached.contentVersion)
            pending.reservation.commit(descriptor.actualBytes, key, descriptor.budgetClass)
            finishPending(pending)
            owner.ownCache[canonicalPath] = cached
            owner.referrers += canonicalPath
            resolve(cached.toResolved(canonicalPath, descriptor, owner.id))
        } catch (error: Throwable) {
            rejectRead(pending, error, reject)
        }
    }

    private fun acquireLease(asset: ResolvedAsset<ByteArray>): ObjectUrlLease {
        val key = ObjectUrlKey(asset.canonicalPath, asset.contentVersion, asset.mime)
        val shared = objectUrls.getOrPut(key) {
            SharedObjectUrl(objectUrlBackend.create(asset.content, asset.mime))
        }
        shared.references += 1
        val releaseState = ObjectUrlReleaseState()
        return ObjectUrlLease(shared.url, key.path, key.version, key.mime) {
            releaseObjectUrl(key, releaseState)
        }
    }

    private fun releaseObjectUrl(key: ObjectUrlKey, state: ObjectUrlReleaseState): Boolean {
        val shared = objectUrls[key] ?: return true
        if (!state.referenceReleased) {
            check(shared.references > 0) { "对象 URL 引用计数已损坏" }
            shared.references -= 1
            state.referenceReleased = true
        }
        if (shared.references > 0) return true
        objectUrlBackend.revoke(shared.url)
        objectUrls.remove(key)
        return true
    }

    private fun reserve(canonicalPath: String, budgetClass: AssetBudgetClass, declaredBytes: Long?): AssetReservation =
        budget.reserve(
            AssetBudgetKey.Resource(canonicalPath, "generation:$generationValue:pending"),
            budgetClass,
            declaredBytes,
        )

    private fun advanceGeneration(startCandidate: Boolean) {
        val previousCandidate = candidateState
        val nextCurrentBytes = if (previousCandidate != null) {
            budget.currentBytes
        } else {
            budget.currentBytes + budget.generationBytes
        }
        generationValue += 1
        cancelPending()
        previousCandidate?.clear()
        budget.startGeneration(nextCurrentBytes)
        candidateState = if (startCandidate) newState() else null
        candidateBudgetRetirement = if (startCandidate) {
            PreviewBudgetRetirement(budget::releasePreviousCurrent)
        } else {
            null
        }
    }

    private fun cancelPending() {
        pendingReads.toList().forEach(PendingRead::cancel)
        versionReads.toList().forEach(AssetSourceReadOperation::cancel)
        versionReads.clear()
    }

    private fun finishPending(pending: PendingRead) {
        if (!pending.finish()) return
        pendingReads -= pending
    }

    private fun rejectRead(pending: PendingRead, error: Throwable, reject: (Throwable) -> Unit) {
        if (!pending.finish()) return
        pendingReads -= pending
        pending.reservation.cancel()
        reject(error)
    }

    private fun ensureActive(expectedGeneration: Long) {
        if (disposed) failResolver("资产解析器已释放")
        if (expectedGeneration != generationValue) failResolver("资产读取 generation 已失效")
    }

    private fun ensureNotDisposed() {
        if (disposed) failResolver("资产解析器已释放")
    }

    private fun externalPath(path: String): String = try {
        ensureNotDisposed()
        AssetPathPolicy.external(path)
    } catch (error: Throwable) {
        throw error
    }

    private fun writeState(): GenerationReadState = candidateState ?: publishedState

    private fun stateOwningReferrer(path: String): GenerationReadState = uniqueOwner(
        candidateState?.takeIf { path in it.referrers },
        publishedState.takeIf { path in it.referrers },
        "内部资产 referrer",
    )

    private fun stateOwningDirectory(directory: String): GenerationReadState = uniqueOwner(
        candidateState?.takeIf { it.ownsDirectory(directory) },
        publishedState.takeIf { it.ownsDirectory(directory) },
        "内部资产 canonical directory provenance",
    )

    private fun uniqueOwner(
        candidate: GenerationReadState?,
        published: GenerationReadState?,
        category: String,
    ): GenerationReadState {
        if (candidate != null && published != null) failResolver("$category 归属多义")
        return candidate ?: published ?: failResolver("$category 未登记")
    }

    private fun stateById(id: Long): GenerationReadState? = when (id) {
        candidateState?.id -> candidateState
        publishedState.id -> publishedState
        else -> null
    }

    private fun ensureStateOwned(state: GenerationReadState) {
        if (stateById(state.id) == null) failResolver("资产读取状态已失效")
    }

    private fun newState(): GenerationReadState = GenerationReadState(stateIdSequence++)

    private fun relativeFilePath(referrer: String, relativePath: String): String =
        relativeDirectoryPath(referrer.substringBeforeLast('/', ""), relativePath)

    private fun relativeDirectoryPath(directory: String, relativePath: String): String =
        AssetPathPolicy.relative(directory, relativePath.removePrefix("./"))
}

private data class CachedAsset(val bytes: ByteArray, val declaredMime: String?, val contentVersion: String) {
    fun toResolved(path: String, descriptor: AssetContentDescriptor, ownerStateId: Long): ResolvedAsset<ByteArray> =
        ResolvedAsset(
            path,
            descriptor.actualBytes,
            descriptor.mime,
            contentVersion,
            bytes,
            CanonicalAssetFileProvenance(path, ownerStateId),
        )
}

private class GenerationReadState(val id: Long) {
    val ownCache = mutableMapOf<String, CachedAsset>()
    val referrers = mutableSetOf<String>()
    val readSet = linkedMapOf<String, PreviewReadAsset>()
    val optionalMissing = linkedMapOf<String, PreviewReadKind>()

    fun ownsDirectory(directory: String): Boolean = referrers.any {
        it.substringBeforeLast('/', "") == directory
    }

    fun clear() {
        ownCache.clear()
        referrers.clear()
        readSet.clear()
        optionalMissing.clear()
    }
}

private class PendingRead(private val operation: AssetSourceReadOperation?, val reservation: AssetReservation) {
    var jpegOperation: AssetJpegCheckOperation? = null
    var cancelled = false
        private set
    private var settled = false

    fun cancel() {
        if (cancelled || settled) return
        cancelled = true
        operation?.cancel()
        jpegOperation?.cancel()
    }

    fun finish(): Boolean {
        if (settled) return false
        settled = true
        return true
    }
}

private data class ObjectUrlKey(val path: String, val version: String, val mime: String)

private class ObjectUrlReleaseState(var referenceReleased: Boolean = false)

private data class SharedObjectUrl(val url: String, var references: Int = 0)

private data class VersionProbeEntry(
    val canonicalPath: String,
    val kind: PreviewReadKind,
    val contentVersion: String?,
)

private fun captureCleanupError(errors: MutableList<Throwable>, action: () -> Unit) {
    try {
        action()
    } catch (error: Throwable) {
        errors += error
    }
}

private fun throwCleanupErrors(errors: List<Throwable>) {
    if (errors.isEmpty()) return
    val primary = errors.first()
    errors.drop(1).forEach(primary::addSuppressed)
    throw primary
}

private const val TEXT_MIME = "text/plain"

private fun validateReadSize(read: AssetSourceRead, readLimit: AssetReadLimit) {
    val classifiedLimit = readLimit.byteLimitFor(read.bytes)
    if (read.declaredBytes != null && read.declaredBytes > classifiedLimit) {
        failResolver("资产声明字节超过预算")
    }
    if (read.bytes.size.toLong() > classifiedLimit) failResolver("资产实际字节超过预算")
}

private fun readLimit(budgetClass: AssetBudgetClass): AssetReadLimit = if (budgetClass == AssetBudgetClass.AICR) {
    AssetReadLimit.AicrOrText
} else {
    AssetReadLimit.Fixed(budgetClass.singleFileLimit())
}

private fun contentVersion(read: AssetSourceRead): String {
    val metadata = read.versionMetadata?.takeIf(String::isNotBlank) ?: "content"
    return "$metadata:${read.bytes.stableHash()}"
}

private fun ByteArray.stableHash(): String {
    var hash = 0x811C9DC5.toInt()
    forEach { byte ->
        hash = hash xor (byte.toInt() and 0xFF)
        hash *= 0x01000193
    }
    return hash.toUInt().toString(16)
}

private fun AssetReferenceKind.preflightClass(): AssetBudgetClass = when (this) {
    AssetReferenceKind.SOURCE_IMPORT,
    AssetReferenceKind.THEME,
    AssetReferenceKind.MOCK,
    -> AssetBudgetClass.TEXT
    AssetReferenceKind.MODEL -> AssetBudgetClass.BBMODEL
    AssetReferenceKind.TEXTURE,
    AssetReferenceKind.BACKGROUND,
    -> AssetBudgetClass.IMAGE
}

private fun AssetReferenceKind.expectedMime(): String? = when (this) {
    AssetReferenceKind.SOURCE_IMPORT -> "text/plain"
    // 主题源为 .allin 文本；历史 .json 主题仍走 JSON MIME
    AssetReferenceKind.THEME -> null
    AssetReferenceKind.MOCK,
    AssetReferenceKind.MODEL,
    -> "application/json"
    AssetReferenceKind.TEXTURE,
    AssetReferenceKind.BACKGROUND,
    -> null
}

private fun validateReferenceKind(path: String, kind: AssetReferenceKind) {
    val lower = path.lowercase()
    val valid = when (kind) {
        AssetReferenceKind.SOURCE_IMPORT -> lower.endsWith(".allin")
        // 主题预设既支持 .allin（生产 ui-themes），也兼容历史 .json 夹具
        AssetReferenceKind.THEME -> lower.endsWith(".allin") || lower.endsWith(".json")
        AssetReferenceKind.MOCK -> lower.endsWith(".mock.json")
        AssetReferenceKind.MODEL -> lower.endsWith(".bbmodel")
        AssetReferenceKind.TEXTURE,
        AssetReferenceKind.BACKGROUND,
        -> lower.endsWith(".png") || lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".webp")
    }
    if (!valid) failResolver("内部资产引用类型与扩展名冲突")
}

private fun AssetBudgetClass.singleFileLimit(): Long = when (this) {
    AssetBudgetClass.TEXT -> AssetBudgetLimits.TEXT_BYTES
    AssetBudgetClass.AICR,
    AssetBudgetClass.BBMODEL,
    AssetBudgetClass.IMAGE,
    -> AssetBudgetLimits.BINARY_BYTES
}

private fun String.isJpeg(): Boolean {
    val lower = lowercase()
    return lower.endsWith(".jpg") || lower.endsWith(".jpeg")
}

private fun PreviewReadKind.probeReadLimit(): AssetReadLimit = when (this) {
    PreviewReadKind.ROOT_BYTES -> AssetReadLimit.AicrOrText
    PreviewReadKind.ROOT_SOURCE,
    PreviewReadKind.SOURCE_IMPORT,
    PreviewReadKind.THEME,
    PreviewReadKind.MOCK,
    -> AssetReadLimit.Fixed(AssetBudgetLimits.TEXT_BYTES)
    PreviewReadKind.MODEL,
    PreviewReadKind.TEXTURE,
    PreviewReadKind.BACKGROUND,
    -> AssetReadLimit.Fixed(AssetBudgetLimits.BINARY_BYTES)
}

private fun Throwable.isNotFound(): Boolean =
    this is AssetSourceException && failure == AssetSourceFailure.NOT_FOUND

private fun <T, R> Promise<T>.flatMap(transform: (T) -> Promise<R>): Promise<R> =
    then(transform).unsafeCast<Promise<R>>()

private fun <T, R> mapResolved(promise: Promise<T>, transform: (T) -> R): Promise<R> = promise.then(transform)

private fun <T> rejected(message: String): Promise<T> = Promise.reject(AssetPolicyException(message))

private fun failResolver(message: String): Nothing = throw AssetPolicyException(message)
